import { Component, computed, inject, input } from '@angular/core';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { LegacyShimsModule } from '../../../../account-shared/legacy-shims/legacy-shims.module';

/**
 * Native port of the AngularJS `ftBoolField` field-editor directive: one `.modal-panel`
 * per (normalised) field with a `.ft-label` and the shared native `<ft-switch>`. The
 * `(stateChange)` writes back to `datasource[field.name]` on the live host object.
 *
 * A field may declare `showWhen: '<other field name>'` — the row then renders only while
 * that field is truthy on the datasource (see {@link isFieldVisible}).
 */
@Component({
    selector: 'ft-bool-field-ng',
    imports: [LegacyShimsModule],
    templateUrl: './ft-bool-field.component.html'
})
export class FtBoolFieldComponent {
    /** The object whose boolean fields the switches toggle (e.g. `data.contest`). */
    readonly datasource = input<any>();
    /** A field name, or `{name,label}`, or a list of either — normalised below. */
    readonly fields = input<any>();
    /** Visual-only `disabled` class + switch disable (undefined for the factory's bool path). */
    readonly disabledField = input<any>();

    private readonly parse = inject(ParseService);

    protected readonly normalizedFields = computed<Array<{ name: string; label: string | null; showWhen?: string }>>(() => {
        // v1 boolField.js `adjustFields`: coerce string entries to {name,label:null}.
        const fields = this.fields();
        const raw = Array.isArray(fields) ? fields : fields == null ? [] : [fields];
        return raw.map((f: any) => (typeof f === 'string' ? { name: f, label: null } : f));
    });

    /**
     * A dependent row's live gate. The v1 hosts `$watch`ed the master flag and rebuilt their
     * `fields` array IN PLACE (`length = 0; push(...)`), so e.g. the place-info "Required" row
     * appeared the moment "Status" was switched on inside the open modal
     * (details.controller.js:65-80, integration.controller.js:89-105). Natively the array reaches
     * us through a signal input, so an in-place mutation never re-renders — instead the dependent
     * row declares `showWhen` and we re-read the LIVE datasource here, which change detection
     * re-evaluates exactly like the v1 digest re-ran the watch. `parse.bool` (not truthiness)
     * matches the v1 watch, so a `'0'` string hides the row as it did on master.
     */
    protected isFieldVisible(field: { showWhen?: string }): boolean {
        if (!field || !field.showWhen) return true;
        return this.parse.bool(this.datasource()?.[field.showWhen]);
    }
}
